/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Builds the curated column layout (WidgetForm default layout) that every cp runtime QL
 *         table widget passes as the 'widgetForm' parameter, so a table shows the German business
 *         columns of its specification instead of every projected attribute. Also holds the
 *         REUSABLE query and column curation of the matrix-row table
 *         (cp.ProvisionTypeMatrixAgent), which has more than one host.
 */
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';

/**
 * One curated column of a cp QL table widget.
 *
 * `field` is the QL field path exactly as the query projects it — `e.<attribute>` for the root
 * entity, `<joinAs>.<attribute>` for a joined one. A path that no join produces renders an EMPTY
 * column at runtime WITHOUT a compile error, so every path here must have a matching join with the
 * same alias in the query it belongs to (fe_core_widget TW-6).
 */
export interface CpWidgetColumn {
    /** QL field path: `e.<attr>` (root) or `<joinAs>.<attr>` (joined entity). */
    field: string;
    /** German column header, with real umlauts. */
    label: string;
}

/**
 * Build the default layout of a CURATED cp table widget — the column set, their order and their
 * German headers — for `WidgetDataParam.create('widgetForm', …)`.
 *
 * WHY EVERY cp RUNTIME TABLE NEEDS ONE: without a `widgetForm` the generic QL table renders EVERY
 * projected attribute, which for `cp.ProvisionContractAgent` alone is 30+ columns of amounts,
 * liability bookkeeping and audit fields — unusable as a worklist. `hideFields` + `fieldsConfigured`
 * are the documented "only the configured fields are shown" switch pair; the repo reference is
 * `features/feature-bm/bd/widget/bd-dunning-proposal-worklist.widget.ts`, which introduced exactly
 * this helper shape after its own table had rendered 84 technical columns.
 *
 * @param columns the visible columns, in the order they should appear
 */
export function buildCpWidgetForm(columns: CpWidgetColumn[]): WidgetForm {

    const layout = new WidgetConfigFormLayout();
    layout.hideFields = true;
    layout.fieldsConfigured = true;

    columns.forEach((column, index) => {
        const field = WidgetConfigFormField.create(column.field, '', '');
        field.uiLabel = column.label;
        field.uiPriority = index;
        layout.addField(field);
    });

    const widgetForm = new WidgetForm();
    widgetForm.setDefaultLayout(layout);

    return widgetForm;
}

/* ═════════════════════════════════════════════════════════════════════════════════════════════
   THE MATRIX-ROW TABLE (cp.ProvisionTypeMatrixAgent) — query and curation, in ONE place
   ═════════════════════════════════════════════════════════════════════════════════════════════

   The rows of a provision matrix are shown on more than one surface: on the navItem "Matrizen" of
   the ProvisionType OBC (level 2 of the selectable master-detail), and in the ProvisionTypeMatrix
   OBC that the inheritance-chain chip of the Beratersicht opens. Both need the SAME explicit joins
   and the SAME German headers, and a second hand-written copy of either would drift silently: a
   QL field path that no join produces renders an EMPTY column without any compile error.

   Hence the query builder and the two column sets live here, next to `buildCpWidgetForm`; the
   hosts differ only in the widget alias, the query name and which column set they pass.

   BOTH HOSTS READ FROM HERE. There is no inline copy of this query or of these columns left in the
   module — verified field for field and header for header when the second host was switched over.
   If a third surface ever needs the same table, it takes the same route; do not paste a literal.

   The two hosts pass DIFFERENT query names, and that is deliberate rather than cosmetic:
   `config.cp.ProvisionTypeMatrixAgent` for the ProvisionType OBC and
   `cp.provisionTypeMatrix.object.rows.{full,side}` for the ProvisionTypeMatrix OBC. Their widget
   ALIASES differ for the same reason, and those aliases are identity: `ui.UiWidget.alias` is a
   globally unique, DB-backed handle that the user's column widths and sorting hang off. Renaming
   one silently discards that state — never "tidy" them into a shared constant.                   */

/**
 * The curated columns of the matrix-row table, FULL variant — 12 columns in the reading order
 * "wer -> wie viel -> wie gerechnet -> wofür -> wann".
 *
 * The matrix column itself is deliberately absent in every variant: each host filters the list ON
 * one matrix, so that column is constant by construction.
 */
export const CP_MATRIX_AGENT_COLUMNS_FULL: CpWidgetColumn[] = [
    {field: 'paa.name', label: 'Vermittler'},
    {field: 'ptg.name', label: 'Vermittlergruppe'},
    {field: 'e.factor', label: 'Faktor'},
    {field: 'e.calcMode', label: 'Berechnungsart'},
    {field: 'e.termMonthsFrom', label: 'Laufzeit von (Monate)'},
    {field: 'e.termMonthsTo', label: 'Laufzeit bis (Monate)'},
    {field: 'e.minEntryAgeYears', label: 'Eintrittsalter von (Jahre)'},
    {field: 'e.maxEntryAgeYears', label: 'Eintrittsalter bis (Jahre)'},
    {field: 'lto.name', label: 'Haftungstyp (Überschreibung)'},
    {field: 'e.startDate', label: 'Gültig ab'},
    {field: 'e.endDate', label: 'Gültig bis'},
    {field: 'e.entityStatus', label: 'Status'}
];

/**
 * The curated columns of the matrix-row table, COMPACT variant — the five that survive in a narrow
 * side drawer. Same field paths and the SAME German headers as the full set, so both surfaces read
 * alike; this is a REDUCTION of {@link CP_MATRIX_AGENT_COLUMNS_FULL}, never a second wording.
 *
 * WHY THESE FIVE:
 *   - `paa.name` + `ptg.name` — the "who" of a row is an XOR of agent and agent group, and QL
 *     projects fields rather than coalescing them, so dropping either side would blank the rows of
 *     the other leg. Both stay; exactly one of them is filled per row.
 *   - `e.factor` + `e.calcMode` — these two never travel apart. A factor without its Berechnungsart
 *     is not a number anyone can read: 12 means twelve monthly premiums under MONTHLY_PREMIUM and
 *     twelve thousandths of the total-term base under PER_MILLE, three orders of magnitude apart.
 *     The module states that rule in the header of `cp-form-field-output-factor.component.ts`
 *     ("the single most expensive misreading this page can produce"), and a compact table does not
 *     get to break it.
 *   - `e.termMonthsFrom` — the band start is what tells several rows of the SAME agent or group
 *     apart; without it the extra rows of an inheritance chain read as duplicates.
 *
 * WHAT IS GONE, and it is a trade-off rather than an oversight: `e.entityStatus`. An inactive row
 * is therefore indistinguishable from an active one HERE. That is the least damaging of the
 * possible cuts — the drawer is a lookup surface reached from a chain chip, while the full view one
 * step away carries the status column — but it IS a cut. Should the status ever matter more than
 * the five-column budget, append it; nothing else in this file has to change.
 */
export const CP_MATRIX_AGENT_COLUMNS_SIDE: CpWidgetColumn[] = [
    {field: 'paa.name', label: 'Vermittler'},
    {field: 'ptg.name', label: 'Vermittlergruppe'},
    {field: 'e.factor', label: 'Faktor'},
    {field: 'e.calcMode', label: 'Berechnungsart'},
    {field: 'e.termMonthsFrom', label: 'Laufzeit von (Monate)'}
];

/**
 * Build the QL view over the rows of ONE provision matrix — EXPLICIT joins, never
 * `resolve_fkey_texts`.
 *
 * WHY THE JOINS ARE SPELLED OUT (the module's reasoning, kept with the code that carries it):
 *
 *   - The agent side is exactly what `resolve_fkey_texts` cannot serve. `cp.ProvisionAgent` has no
 *     name column of its own and derives its display name from a getter over `am.Agent`
 *     (`ProvisionAgent.java:299-320`); the resolver only joins targets that declare the name
 *     themselves and never reaches two hops. Hence `provisionAgent` -> `agent`, and the label comes
 *     from the SELECTED column `paa.name` instead of the getter — whose
 *     `LazyInitializationException` branch falls back to the id AS THE LABEL. On this path that
 *     fallback is structurally unreachable.
 *   - The group side IS resolvable, but only under the positional alias `fkeyt_e_<N>`, which shifts
 *     whenever a relation is added to the entity and cannot be pinned without a live backend.
 *   - Both sides of the XOR are LEFT joins: exactly one of them is set per row.
 *
 * @param queryName              QL query name — one per host, so two surfaces never share a query
 *                               identity
 * @param provisionTypeMatrixId  the matrix whose rows are listed; the query is fixed-filtered on it
 */
export function buildCpMatrixAgentQuery(queryName: string, provisionTypeMatrixId: number): QlQueryDto {

    return {
        name: queryName,
        type: 'view',
        start: {
            type: 'entity',
            name: 'cp.ProvisionTypeMatrixAgent',
            as: 'e',
            joins: [
                // Two hops — cp.ProvisionAgent has no declared name attribute, am.Agent does.
                {
                    type: 'entity', name: 'provisionAgent', as: 'pa',
                    joinType: 'left', fields: [{name: 'id'}],
                    joins: [
                        {
                            type: 'entity', name: 'agent', as: 'paa',
                            joinType: 'left', fields: [{name: 'name'}]
                        }
                    ]
                },
                // Both sides of the XOR are LEFT joins: exactly one of them is set per row.
                {
                    type: 'entity', name: 'provisionTypeGroup', as: 'ptg',
                    joinType: 'left', fields: [{name: 'name'}]
                },
                {
                    type: 'entity', name: 'liabilityTypeOverwrite', as: 'lto',
                    joinType: 'left', fields: [{name: 'name'}]
                }
            ]
        },
        fixedFilters: [
            FilterCriteria.create('e.provisionTypeMatrixId',
                FilterCriteria.cOperatorEqual, provisionTypeMatrixId)
        ],
        sortings: [new Sorting('e.id', false)]
    };
}

/* ═════════════════════════════════════════════════════════════════════════════════════════════
   THE GROUP-MEMBERSHIP TABLE (cp.ProvisionAgentGroup) — same problem, same remedy
   ═════════════════════════════════════════════════════════════════════════════════════════════

   `cp.ProvisionAgentGroup` is a pure LINK entity: it has no name of its own and describes the
   periodised membership of one agent in one provision type, optionally in one group of that type.
   Every column a reader cares about therefore lives one or two hops away, which is exactly the
   situation `resolve_fkey_texts` cannot serve for the agent leg — see the join comment below.    */

/**
 * The curated columns of the group-membership table.
 *
 * THE LABELS ARE THE ENTITY'S OWN. Each one is copied from the `@UiFieldInfo(uiLabel = …)` of the
 * field it renders (`ProvisionAgentGroup.java`): "Vermittler" (:115), "Provisionsart" (:139),
 * "Vermittlergruppe" (:163), "Start" (:190), "Ende (automatisch)" (:227). That is deliberate and
 * it is the whole point: the table already showed these headings before it became a QL view, so
 * switching it over changes what the CELLS contain and nothing about what the HEADINGS say. Do not
 * "harmonise" them with the matrix table's "Gültig ab" / "Gültig bis" — those belong to a different
 * entity whose period bounds are both user-maintained, whereas `endDate` here is system-set, which
 * is precisely what its heading tells the reader.
 *
 * "Status" follows the module's other curated tables; it renders `AuditableEntity.entityStatus`.
 *
 * Provisionsart is carried even though only Berater and Gruppe were asked for: the same
 * (Berater, Gruppe) pair may exist once PER PROVISION TYPE — the provision type is half of the
 * business key (`@EntityFieldBusinessKey` on both FKs, `ProvisionAgentGroup.java:114/138`) — so
 * without that column two legitimate rows read as a duplicate.
 */
export const CP_PROVISION_AGENT_GROUP_COLUMNS: CpWidgetColumn[] = [
    {field: 'paa.name', label: 'Vermittler'},
    {field: 'pt.name', label: 'Provisionsart'},
    {field: 'ptg.name', label: 'Vermittlergruppe'},
    {field: 'e.startDate', label: 'Start'},
    {field: 'e.endDate', label: 'Ende (automatisch)'},
    {field: 'e.entityStatus', label: 'Status'}
];

/**
 * Build the QL view over the periodised group memberships (`cp.ProvisionAgentGroup`).
 *
 * WHY THE AGENT LEG NEEDS TWO EXPLICIT HOPS — the same defect as on the matrix-row table:
 * `cp.ProvisionAgent` declares no name column and derives its display name from a getter over
 * `am.Agent` that falls back to `id.toString()`; `resolve_fkey_texts` only joins targets that
 * declare the name themselves and never reaches two hops. Left as an unresolved FK, the
 * "Vermittler" column shows a NUMBER — which is the defect this query exists to remove. The name
 * comes from the SELECTED column `paa.name`, so the id fallback is structurally unreachable.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * EVERY JOIN IS `left`, AND FOR `provisionTypeGroup` THAT IS LOAD-BEARING, NOT STYLE
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `provisionTypeGroup` is `@ManyToOne(optional = true)` (`ProvisionAgentGroup.java:166`) and a NULL
 * there is a MEANINGFUL BUSINESS STATE, not an unfinished row. The entity javadoc states it
 * outright: *"A null provisionTypeGroup means the agent is a member of the provision type without
 * belonging to a group — only individual agent rows of the matrix can then apply."*
 *
 * An `inner` join would drop exactly those memberships, and it would do so SILENTLY: the table
 * would still render, still look sorted and complete, and the rows describing agents who are paid
 * only through individual matrix lines would simply not be there. A reader has no way to notice an
 * absence. `left` on all three legs is therefore mandatory — for the two `optional = false` legs it
 * costs nothing and additionally keeps an orphaned row visible instead of vanishing it.
 *
 * NO fixed filter and NO sorting: the config table lists every row, and it carried no default
 * ordering before this query either. Adding one here would change what the user sees beyond the
 * naming fix this query is for.
 *
 * @param queryName  QL query name — one per host, so two surfaces never share a query identity
 */
export function buildCpProvisionAgentGroupQuery(queryName: string): QlQueryDto {

    return {
        name: queryName,
        type: 'view',
        start: {
            type: 'entity',
            name: 'cp.ProvisionAgentGroup',
            as: 'e',
            joins: [
                // Two hops — cp.ProvisionAgent has no declared name attribute, am.Agent does.
                {
                    type: 'entity', name: 'provisionAgent', as: 'pa',
                    joinType: 'left', fields: [{name: 'id'}],
                    joins: [
                        {
                            type: 'entity', name: 'agent', as: 'paa',
                            joinType: 'left', fields: [{name: 'name'}]
                        }
                    ]
                },
                {
                    type: 'entity', name: 'provisionType', as: 'pt',
                    joinType: 'left', fields: [{name: 'name'}]
                },
                // MUST stay `left` — a NULL group is a valid membership, see the block above.
                {
                    type: 'entity', name: 'provisionTypeGroup', as: 'ptg',
                    joinType: 'left', fields: [{name: 'name'}]
                }
            ]
        }
    };
}
